import type { BillingOverview } from "@contract/firm/billing";
import { SimpleGrid, Stack } from "@mantine/core";
import * as React from "react";
import * as billing from "../api/billing";
import { ApiError } from "../api/client";
import { useMe } from "../api/MeContext";
import { openCheckout } from "../billing/paddleCheckout";
import { Notice } from "../ui/Notice";
import { PageLoader } from "../ui/PageLoader";
import { PANEL_SPACE } from "../ui/tokens";
import { PlanCompareCard } from "./billing/PlanCompareCard";
import { usePlanChange } from "./billing/usePlanChange";
import { UsageCard } from "./home/UsageCard";
import { YourDetailsCard } from "./home/YourDetailsCard";
import { billingBanner, billingState } from "./planBillingModel";

/** Plan & Billing (firm-model spec §10.2, §7; admin only, from the account menu; frame
 * 15199:6383, Dan 2026-10-04): the state's banner, Your Details beside Usage, then Change
 * Your Plan. Billing details and cancelling live in Paddle's portal ("Manage billing"). */
export function PlanBillingPage({
  api = billing,
  checkout = openCheckout,
  now = new Date(),
}: {
  api?: typeof billing;
  checkout?: typeof openCheckout;
  now?: Date;
}) {
  const { state, reload } = useMe();
  const [overview, setOverview] = React.useState<BillingOverview | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [upgraded, setUpgraded] = React.useState(false);
  const [nonce, setNonce] = React.useState(0);

  React.useEffect(() => {
    let alive = true;
    api
      .fetchBilling()
      .then((o) => alive && setOverview(o))
      .catch((e: unknown) => alive && setError(e instanceof ApiError ? e.message : "We couldn't load your plan. Try again."));
    return () => {
      alive = false;
    };
  }, [nonce]);

  const refresh = () => {
    reload();
    setNonce((n) => n + 1);
  };
  const change = usePlanChange({
    onChanged: refresh,
    onUpgraded: () => {
      setUpgraded(true);
      refresh();
    },
    api,
    checkout,
  });

  if (state.status !== "ready") return null;
  const me = state.me;
  if (error) return <Notice tone="danger">{error}</Notice>;
  if (!overview) return <PageLoader />;

  const pageState = billingState(overview, me.isBeta);
  const banner = upgraded ? null : billingBanner(pageState, overview, me, now);

  return (
    <Stack gap={PANEL_SPACE.card}>
      {upgraded ? (
        <Notice tone="success" title="You're on Team" onClose={() => setUpgraded(false)}>
          Your seats and credits update in a moment. Invite your team from Team Members.
        </Notice>
      ) : null}
      {banner ? <Notice tone={banner.tone}>{banner.text}</Notice> : null}
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing={PANEL_SPACE.card} verticalSpacing={PANEL_SPACE.card}>
        <YourDetailsCard me={me} now={now} onRenamed={reload} />
        <div>
          <UsageCard me={me} now={now} />
        </div>
      </SimpleGrid>
      <PlanCompareCard me={me} overview={overview} state={pageState} change={change} />
    </Stack>
  );
}
